import { createMemo, createResource, createRoot, createSignal } from "solid-js";
import type { SiteNode, SiteTree } from "@energyplatform/client";
import { PERIODS, periodRange, type Period } from "./periods.ts";
import { api, session } from "./services.ts";

export { PERIODS, periodRange, type Period } from "./periods.ts";
export { depthFirst, pathTo } from "./tree.ts";

// The context bar's state (doc: prototype shell): which part of the tree the screens look at, and over which period.

const SCOPE_KEY = "ep.scope";
const PERIOD_KEY = "ep.period";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Not remembered; the choice still applies until reload.
  }
}

const initialPeriod = (): Period => {
  const saved = read(PERIOD_KEY);
  return (PERIODS as readonly string[]).includes(saved ?? "")
    ? (saved as Period)
    : "today";
};

const state = createRoot(() => {
  const [period, setPeriodSignal] = createSignal<Period>(initialPeriod());
  const [scopeId, setScopeSignal] = createSignal<string | null>(
    read(SCOPE_KEY),
  );
  // Loaded once signed in; the asset screen reloads it after changes.
  const [tree, { refetch, mutate }] = createResource(
    () => session().status === "signedIn" || undefined,
    () => api.sites.tree(),
  );
  const range = createMemo(() => periodRange(period()));

  /** The chosen node, or the first root when nothing (or a node that no longer exists) was chosen. */
  const scope = createMemo<SiteNode | undefined>(() => {
    const nodes = tree()?.nodes ?? [];
    return (
      nodes.find((n) => n.id === scopeId()) ??
      nodes.find((n) => n.parentId === null)
    );
  });

  return {
    period,
    setPeriodSignal,
    scopeId,
    setScopeSignal,
    tree,
    refetch,
    mutate,
    range,
    scope,
  };
});

export const period = state.period;
export const range = state.range;
export const siteTree = state.tree;
export const scope = state.scope;

export function setPeriod(next: Period): void {
  state.setPeriodSignal(next);
  write(PERIOD_KEY, next);
}

export function setScope(nodeId: string): void {
  state.setScopeSignal(nodeId);
  write(SCOPE_KEY, nodeId);
}

/** Reloads the tree after changes on the asset screen. */
export async function reloadTree(): Promise<SiteTree | undefined> {
  api.invalidate("/api/v1/nodes/tree");
  return (await state.refetch()) ?? undefined;
}
